import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, PaperPlaneTilt, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { ProductSelect } from "../components/ProductSelect";

const emptyItem = () => ({ _key: crypto.randomUUID(), product_name: "", quantity: "" });

export default function CustomerNewOrder() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([emptyItem()]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const setItem = (i, key, value) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)));

  const total = items.reduce((s, it) => s + (parseFloat(it.quantity) || 0), 0);

  const submit = async () => {
    const parsed = items.map((it) => ({ product_name: String(it.product_name).trim(), quantity: parseFloat(it.quantity) }));
    if (!parsed.length || parsed.some((it) => !it.product_name || !(it.quantity > 0))) {
      toast.error("Every line needs a product and quantity > 0");
      return;
    }
    setSaving(true);
    try {
      await api.post("/customer/order-requests", { items: parsed, notes: notes.trim() || null });
      toast.success("Order submitted! DCN will review and confirm it shortly.");
      navigate("/portal");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="customer-new-order-page" className="max-w-xl space-y-6">
      <Link to="/portal" data-testid="portal-back-link"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 transition-colors duration-150 hover:text-slate-900">
        <ArrowLeft size={15} /> Back to my orders
      </Link>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">Place New Order</h1>
        <p className="mt-1 text-sm text-slate-500">Choose products and quantities. Your order will be confirmed by the DCN team.</p>
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-5">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Items</label>
        <div className="mt-2 space-y-2">
          {items.map((it, i) => (
            <div key={it._key} className="flex items-center gap-2">
              <div className="min-w-0 flex-1 [&>button]:mt-0">
                <ProductSelect products={products} value={it.product_name} testId={`portal-item-product-${i}`}
                  allowCustom={false} placeholder="Select product"
                  onChange={(v) => setItem(i, "product_name", v)} />
              </div>
              <input data-testid={`portal-item-qty-${i}`} type="number" min="0" step="any" value={it.quantity}
                onChange={(e) => setItem(i, "quantity", e.target.value)} placeholder="Qty"
                className="w-20 rounded-md border border-slate-300 px-2 py-2 text-right font-mono text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
              {items.length > 1 && (
                <button data-testid={`portal-item-remove-${i}`}
                  onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))}
                  className="text-slate-400 transition-colors duration-150 hover:text-red-600">
                  <X size={15} />
                </button>
              )}
            </div>
          ))}
        </div>
        <button type="button" data-testid="portal-add-item-button"
          onClick={() => setItems((p) => [...p, emptyItem()])}
          className="mt-2 text-sm font-medium text-slate-600 underline-offset-4 hover:underline">
          + Add another product
        </button>

        <div className="mt-4">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes (optional)</label>
          <textarea data-testid="portal-notes-input" value={notes} rows={2}
            onChange={(e) => setNotes(e.target.value)} placeholder="Delivery instructions, preferred dates…"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4">
          <p className="text-sm text-slate-500">Total quantity: <span className="font-mono font-bold text-slate-900" data-testid="portal-total-qty">{total}</span></p>
          <button data-testid="portal-submit-order-button" onClick={submit} disabled={saving}
            className="flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700 disabled:opacity-60">
            <PaperPlaneTilt size={15} weight="bold" />
            {saving ? "Submitting…" : "Submit Order"}
          </button>
        </div>
      </div>
    </div>
  );
}
